// RichTextControls — super/subscript ("Raised & lowered") and fit-to-box.
// Rendered only for text objects, in two hostings:
//   • sidebar (TextCustomizePanel grouped mode): mounted TWICE with a
//     `section` prop ('script' | 'fit'), one accordion each — the accordion
//     head carries the title, so the subheads are skipped.
//   • styles-modal rail (no `section`): both blocks with their own subheads.
//
// The old 'emphasize' slice (per-selection color/size/bold/italic/underline
// + per-char highlight chips + Clear emphasis) was REMOVED 2026-07-19 (owner:
// "The emphasize accordion should not be there at all") — the STANDARD
// controls are selection-aware now: the engine's routeToLiveSelection routes
// toolbar font/size/B/I/U and the Color section's solid picks to the
// highlighted characters, and the Color section grew the quiet "Reset
// character styling" link (FillSection) that replaced Clear emphasis.
//
// What remains here drives fabric's native per-character styles (obj.styles)
// through the engine's setTextScript, reading `objProps.selectionStyle`
// FIRST and falling back to the object prop — selectionStyle is sparse (a
// key is present only when every character in the range carries the same
// override).
//
// Range: the engine styles the highlighted characters while the teacher is
// editing, else the whole string (textSelectionRange).
import React from 'react';

export default function RichTextControls({ objProps, apply, editor, section }) {
  const sel = objProps.selectionStyle || {};
  // 3D (fx-extrude) selection: raised/lowered does not exist in the 3D
  // geometry (one baseline) — gate DISABLED with the hint below instead of
  // doing nothing silently. (2026-07-17 lane.)
  const on3d = !!objProps.is3dActive;

  // Sidebar accordions host one slice each; the modal renders both.
  const showScript = !section || section === 'script';
  const showFit = !section || section === 'fit';
  const bare = !!section; // accordion head owns the title — no subheads

  const deltaY = sel.deltaY ?? 0;
  const script = deltaY < 0 ? 'super' : deltaY > 0 ? 'sub' : 'normal';

  return (
    <div className="cce-inspector-section cce-emph-section">
      {/* Raised / lowered characters (H₂O, x²) — 2D-only: the bake has one
          baseline. */}
      {showScript && (
        <>
          {!bare && <div className="cce-fx-subhead">Raised &amp; lowered</div>}
          <div className="cce-emph-row">
            <button
              type="button"
              className={`cce-fx-chip${script === 'super' ? ' active' : ''}`}
              title="Raise above the line, like the 2 in x²"
              disabled={on3d}
              onClick={() => apply(() => editor?.setTextScript('super'), 'now')}
            >
              x²
            </button>
            <button
              type="button"
              className={`cce-fx-chip${script === 'sub' ? ' active' : ''}`}
              title="Lower below the line, like the 2 in H₂O"
              disabled={on3d}
              onClick={() => apply(() => editor?.setTextScript('sub'), 'now')}
            >
              x₂
            </button>
            <button
              type="button"
              className={`cce-fx-chip${script === 'normal' ? ' active' : ''}`}
              title="Put the characters back on the line"
              disabled={on3d}
              onClick={() => apply(() => editor?.setTextScript('normal'), 'now')}
            >
              Normal
            </button>
          </div>
          {on3d && (
            <div className="cce-hint">
              Raised and lowered letters work on flat text — untick 3D under
              Effects to use them.
            </div>
          )}
        </>
      )}

      {/* Fit — whole-object, never the selected run: fontSize on the OBJECT is
          what wraps the box.
          ONE button, on purpose. A fabric Textbox can never be narrower than
          its own text (its width clamps up to the longest word on every
          re-wrap) and its height is derived from the text — so there is no
          persistent "text overflows its box" state for a Shrink to undo, and a
          Shrink control here would be a button that never does anything. This
          sizes the text to the box the teacher dragged, in both directions. */}
      {showFit && (
        <>
          {!bare && <div className="cce-fx-subhead">Fit</div>}
          <div className="cce-emph-row">
            <button
              type="button"
              className="cce-fx-chip"
              title="Make the text as big as its box allows"
              onClick={() => apply(() => editor?.autoFitText('grow'), 'now')}
            >
              Fit text to its box
            </button>
          </div>
        </>
      )}
    </div>
  );
}
